import React, { useState, useRef, useEffect, type ReactNode } from "react";
import { type StaticImageData } from "next/image";
import {
  Accordion,
  AccordionItem,
  AccordionPanel,
  Flex,
  Box,
  type ExpandedIndex,
  type FlexProps,
} from "@chakra-ui/react";
import { useInView } from "framer-motion";
import { Balancer } from "react-wrap-balancer";
import { AccordionButton } from "./accordion-button";
import { AnimatedImage } from "./animated-image";
import { MobileImage } from "./mobile-image";

export type AccordionItemProps = {
  image?: StaticImageData;
  imageAlt?: string;
  customFigure?: ReactNode;
  eyebrow?: string;
  label: string;
  content: string;
};

type AccordionProps = {
  align: "left" | "right";
  items: AccordionItemProps[];
} & FlexProps;

const animationDuration = 15;

export function AnimatedAccordion({ align, items, ...props }: AccordionProps) {
  const accordionContainer = useRef<HTMLDivElement>(null);
  const isInView = useInView(accordionContainer, { once: true, amount: 0.2 });
  const [animationActive, setAnimationActive] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [counter, setCounter] = useState(animationDuration);

  const activeItem = items[activeIndex];

  const startCounter = () => {
    setCounter(animationDuration);
    setAnimationActive(true);
  };

  const handleChange = (expandedIndex: number) => {
    if (expandedIndex >= 0 && expandedIndex !== activeIndex) {
      setActiveIndex(expandedIndex);
      startCounter();
    }
  };

  // Pause the animation when user is not looking to save resources
  useEffect(() => {
    if (!isInView) {
      setAnimationActive(false);
      return;
    }

    startCounter();
  }, [isInView]);

  // Set animation timing, handle accordion panel switching
  useEffect(() => {
    if (animationActive && counter > 0) {
      const timer = setInterval(() => {
        setCounter(counter - 1);
      }, 1000);

      return () => {
        if (timer) {
          clearTimeout(timer);
        }
      };
    }

    if (counter === 0) {
      const nextTabIndex = activeIndex === items.length - 1 ? 0 : activeIndex + 1;
      setActiveIndex(nextTabIndex);
      startCounter();
    } else if (!animationActive && counter !== 0) {
      clearInterval(counter);
    }
  }, [counter, animationActive, activeIndex, items.length]);

  return (
    <Flex direction={align === "right" ? "row" : "row-reverse"} gap={{ base: 12, lg: 16, xl: 32 }} {...props}>
      <Box display={{ base: "none", md: "block" }} flex={1}>
        <AnimatedImage {...activeItem} />
      </Box>

      <Box flex={1}>
        <Accordion
          ref={accordionContainer}
          index={activeIndex}
          /**
           * ExpandedIndex: number | number[] sets the type based on if the accordion `allowMultiple` is set to true.
           * This component does not `allowMultiple`, so it will always be a `number`.
           */
          onChange={(expandedIndex: ExpandedIndex) => {
            handleChange(expandedIndex as number);
          }}
        >
          {items.map((item) => (
            <AccordionItem
              key={item.label}
              py={{ base: 6, xl: 9 }}
              borderTopWidth="1.5px"
              borderColor="base.border"
              _last={{ borderBottomWidth: "1.5px" }}
            >
              {({ isExpanded }) => (
                <>
                  <AccordionButton
                    eyebrow={item.eyebrow}
                    label={item.label}
                    isExpanded={isExpanded}
                    isInView={isInView}
                    animationDuration={animationDuration}
                  />

                  <AccordionPanel p={0} mt={5} fontSize={{ base: "md", md: "lg" }}>
                    <Balancer>{item.content}</Balancer>

                    <MobileImage {...item} />
                  </AccordionPanel>
                </>
              )}
            </AccordionItem>
          ))}
        </Accordion>
      </Box>
    </Flex>
  );
}
